import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Divider,
  Paper,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  Link
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GavelIcon from '@mui/icons-material/Gavel';
import html2pdf from 'html2pdf.js';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`taxa-tabpanel-${index}`}
      aria-labelledby={`taxa-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TaxeJudiciare = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // State pentru procese civile
  const [tipProces, setTipProces] = useState('patrimonial');
  const [valoareCerere, setValoareCerere] = useState('');
  const [tipInstanta, setTipInstanta] = useState('judecatorie');
  const [tipCale, setTipCale] = useState('prima_instanta');
  const [tipCerereNepatrimoniala, setTipCerereNepatrimoniala] = useState('divort');
  
  // State pentru procese comerciale
  const [valoareComerciala, setValoareComerciala] = useState('');
  const [tipProcesComercial, setTipProcesComercial] = useState('comercial_general');
  
  // State pentru procese administrative
  const [tipProcesAdministrativ, setTipProcesAdministrativ] = useState('contencios_general');
  const [valoareAdministrativa, setValoareAdministrativa] = useState('');
  
  // State pentru rezultate
  const [rezultatCivil, setRezultatCivil] = useState(null);
  const [rezultatComercial, setRezultatComercial] = useState(null);
  const [rezultatAdministrativ, setRezultatAdministrativ] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Taxe fixe pentru cereri nepatrimoniale
  const taxeNepatrimoniale = {
    divort: { taxa: 200, descriere: 'Divorț' },
    adopție: { taxa: 100, descriere: 'Adopție' },
    tutela: { taxa: 50, descriere: 'Tutela/Curatela' },
    schimbare_nume: { taxa: 100, descriere: 'Schimbare nume' },
    declarare_deces: { taxa: 30, descriere: 'Declarare deces' },
    interdictie: { taxa: 100, descriere: 'Punere sub interdicție' },
    rectificare_act: { taxa: 50, descriere: 'Rectificare act stare civilă' }
  };

  // Taxe pentru procese administrative
  const taxeAdministrative = {
    contencios_general: { taxa: 30, descriere: 'Contencios administrativ general' },
    contencios_fiscal: { taxa: 100, descriere: 'Contencios fiscal' },
    contencios_urbanistic: { taxa: 200, descriere: 'Contencios urbanistic' },
    contencios_functionari: { taxa: 50, descriere: 'Contencios funcționari publici' },
    anulare_act_administrativ: { taxa: 100, descriere: 'Anulare act administrativ' }
  };

  // Calculează taxa pentru procese civile conform OUG 80/2013 art. 3
  useEffect(() => {
    if (tipProces === 'patrimonial' && valoareCerere) {
      const valoare = parseFloat(valoareCerere);
      
      if (valoare > 0) {
        let taxaBaza;
        let descriereCalcul;
        
        // Aplicăm formula din OUG 80/2013 art. 3
        if (valoare <= 500) {
          taxaBaza = Math.max(valoare * 0.08, 20); // 8%, min 20 lei
          descriereCalcul = `Până la 500 lei: ${valoare.toFixed(2)} × 8% = ${(valoare * 0.08).toFixed(2)} lei (minim 20 lei) → ${taxaBaza.toFixed(2)} lei`;
        } else if (valoare <= 5000) {
          taxaBaza = 40 + (valoare - 500) * 0.07; // 40 lei + 7% pentru ce depășește 500 lei
          descriereCalcul = `501-5.000 lei: 40 + (${valoare.toFixed(2)} - 500) × 7% = 40 + ${((valoare - 500) * 0.07).toFixed(2)} = ${taxaBaza.toFixed(2)} lei`;
        } else if (valoare <= 25000) {
          taxaBaza = 355 + (valoare - 5000) * 0.05; // 355 lei + 5% pentru ce depășește 5.000 lei
          descriereCalcul = `5.001-25.000 lei: 355 + (${valoare.toFixed(2)} - 5.000) × 5% = 355 + ${((valoare - 5000) * 0.05).toFixed(2)} = ${taxaBaza.toFixed(2)} lei`;
        } else if (valoare <= 50000) {
          taxaBaza = 1355 + (valoare - 25000) * 0.03; // 1.355 lei + 3% pentru ce depășește 25.000 lei
          descriereCalcul = `25.001-50.000 lei: 1.355 + (${valoare.toFixed(2)} - 25.000) × 3% = 1.355 + ${((valoare - 25000) * 0.03).toFixed(2)} = ${taxaBaza.toFixed(2)} lei`;
        } else if (valoare <= 250000) {
          taxaBaza = 2105 + (valoare - 50000) * 0.02; // 2.105 lei + 2% pentru ce depășește 50.000 lei
          descriereCalcul = `50.001-250.000 lei: 2.105 + (${valoare.toFixed(2)} - 50.000) × 2% = 2.105 + ${((valoare - 50000) * 0.02).toFixed(2)} = ${taxaBaza.toFixed(2)} lei`;
        } else {
          taxaBaza = 6105 + (valoare - 250000) * 0.01; // 6.105 lei + 1% pentru ce depășește 250.000 lei
          descriereCalcul = `Peste 250.000 lei: 6.105 + (${valoare.toFixed(2)} - 250.000) × 1% = 6.105 + ${((valoare - 250000) * 0.01).toFixed(2)} = ${taxaBaza.toFixed(2)} lei`;
        }
        
        let taxaFinala = taxaBaza;
        
        // Aplicăm reduceri/majorări în funcție de calea de atac
        if (tipCale === 'apel') {
          taxaFinala = taxaBaza * 0.5;
          descriereCalcul += `\nApel: ${taxaBaza.toFixed(2)} × 50% = ${taxaFinala.toFixed(2)} lei`;
        } else if (tipCale === 'recurs') {
          taxaFinala = taxaBaza;
          descriereCalcul += `\nRecurs: ${taxaBaza.toFixed(2)} × 100% = ${taxaFinala.toFixed(2)} lei`;
        }
        
        setRezultatCivil({
          tipProces: 'Proces civil patrimonial',
          valoareCerere: valoare.toFixed(2),
          tipInstanta: tipInstanta,
          tipCale: tipCale,
          taxaCalculata: taxaFinala.toFixed(2),
          descriereCalcul: descriereCalcul,
          bazaLegala: 'OUG nr. 80/2013, art. 3'
        });
      }
    } else if (tipProces === 'nepatrimonial') {
      const taxaInfo = taxeNepatrimoniale[tipCerereNepatrimoniala];
      
      let taxaFinala = taxaInfo.taxa;
      let descriereCalcul = `Taxa fixă pentru ${taxaInfo.descriere}: ${taxaInfo.taxa} lei`;
      
      if (tipCale === 'apel') {
        taxaFinala = taxaInfo.taxa * 0.5;
        descriereCalcul += `\nApel: ${taxaInfo.taxa} × 50% = ${taxaFinala.toFixed(2)} lei`;
      } else if (tipCale === 'recurs') {
        taxaFinala = taxaInfo.taxa;
        descriereCalcul += `\nRecurs: ${taxaInfo.taxa} × 100% = ${taxaFinala.toFixed(2)} lei`;
      }
      
      setRezultatCivil({
        tipProces: `Proces civil nepatrimonial - ${taxaInfo.descriere}`,
        valoareCerere: 'N/A',
        tipInstanta: tipInstanta,
        tipCale: tipCale,
        taxaCalculata: taxaFinala.toFixed(2),
        descriereCalcul: descriereCalcul,
        bazaLegala: 'OUG nr. 80/2013, art. 4'
      });
    } else {
      setRezultatCivil(null);
    }
  }, [tipProces, valoareCerere, tipInstanta, tipCale, tipCerereNepatrimoniala]);

  // Calculează taxa pentru procese comerciale
  useEffect(() => {
    if (valoareComerciala && tipProcesComercial) {
      const valoare = parseFloat(valoareComerciala);
      
      if (valoare > 0) {
        let taxaCalculata;
        let descriereCalcul;
        let bazaLegala;
        
        if (tipProcesComercial === 'comercial_general') {
          taxaCalculata = valoare * 0.02; // 2%
          if (taxaCalculata < 30) taxaCalculata = 30;
          if (taxaCalculata > 47500) taxaCalculata = 47500;
          
          descriereCalcul = `Taxa comercială: ${valoare.toFixed(2)} × 2% = ${taxaCalculata.toFixed(2)} lei`;
          bazaLegala = 'OUG nr. 80/2013, art. 3';
        } else if (tipProcesComercial === 'insolventa') {
          // Taxa pentru insolvență se calculează în funcție de valoarea activului
          if (valoare <= 50000) {
            taxaCalculata = 500;
          } else if (valoare <= 500000) {
            taxaCalculata = 1000;
          } else if (valoare <= 1000000) {
            taxaCalculata = 2000;
          } else {
            taxaCalculata = 5000;
          }
          
          descriereCalcul = `Taxa pentru procedura insolvenței (activ ${valoare.toFixed(2)} lei): ${taxaCalculata} lei`;
          bazaLegala = 'Legea nr. 85/2014, art. 71';
        } else if (tipProcesComercial === 'arbitraj') {
          taxaCalculata = valoare * 0.01; // 1% pentru arbitraj
          if (taxaCalculata < 100) taxaCalculata = 100;
          if (taxaCalculata > 10000) taxaCalculata = 10000;
          
          descriereCalcul = `Taxa arbitraj: ${valoare.toFixed(2)} × 1% = ${taxaCalculata.toFixed(2)} lei`;
          bazaLegala = 'Regulamentul de arbitraj comercial';
        }
        
        setRezultatComercial({
          tipProces: 'Proces comercial',
          valoareCerere: valoare.toFixed(2),
          tipProcesComercial: tipProcesComercial,
          taxaCalculata: taxaCalculata.toFixed(2),
          descriereCalcul: descriereCalcul,
          bazaLegala: bazaLegala
        });
      }
    } else {
      setRezultatComercial(null);
    }
  }, [valoareComerciala, tipProcesComercial]);

  // Calculează taxa pentru procese administrative
  useEffect(() => {
    if (tipProcesAdministrativ) {
      let taxaCalculata;
      let descriereCalcul;
      let bazaLegala = 'Legea nr. 554/2004, art. 21';
      
      if (tipProcesAdministrativ === 'contencios_fiscal' && valoareAdministrativa) {
        const valoare = parseFloat(valoareAdministrativa);
        if (valoare > 0) {
          taxaCalculata = Math.min(valoare * 0.01, 5000); // 1% maxim 5000 lei
          descriereCalcul = `Taxa contencios fiscal: ${valoare.toFixed(2)} × 1% = ${taxaCalculata.toFixed(2)} lei (max 5000 lei)`;
        }
      } else {
        const taxaInfo = taxeAdministrative[tipProcesAdministrativ];
        taxaCalculata = taxaInfo.taxa;
        descriereCalcul = `Taxa fixă pentru ${taxaInfo.descriere}: ${taxaInfo.taxa} lei`;
      }
      
      if (taxaCalculata !== undefined) {
        setRezultatAdministrativ({
          tipProces: 'Proces administrativ',
          tipProcesAdministrativ: tipProcesAdministrativ,
          valoareCerere: valoareAdministrativa || 'N/A',
          taxaCalculata: taxaCalculata.toFixed(2),
          descriereCalcul: descriereCalcul,
          bazaLegala: bazaLegala
        });
      }
    } else {
      setRezultatAdministrativ(null);
    }
  }, [tipProcesAdministrativ, valoareAdministrativa]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const resetCalculatorCivil = () => {
    setTipProces('patrimonial');
    setValoareCerere('');
    setTipInstanta('judecatorie');
    setTipCale('prima_instanta');
    setTipCerereNepatrimoniala('divort');
    setRezultatCivil(null);
  };

  const resetCalculatorComercial = () => {
    setValoareComerciala('');
    setTipProcesComercial('comercial_general');
    setRezultatComercial(null);
  };

  const resetCalculatorAdministrativ = () => {
    setTipProcesAdministrativ('contencios_general');
    setValoareAdministrativa('');
    setRezultatAdministrativ(null);
  };

  const copiazaRezultat = (rezultat, tip) => {
    if (rezultat) {
      const textDeCopiat = `${rezultat.tipProces} - Taxa judiciară: ${rezultat.taxaCalculata} lei. ${rezultat.descriereCalcul}. Baza legală: ${rezultat.bazaLegala}`;
      navigator.clipboard.writeText(textDeCopiat).then(() => {
        setOpenSnackbar(true);
      });
    }
  };

  const exportaPDF = (rezultat, tip) => {
    if (rezultat) {
      const element = document.createElement('div');
      
      element.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h2 style="text-align: center; color: #333; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
            Calculator Taxe Judiciare - ${rezultat.tipProces}
          </h2>
          
          <div style="margin: 20px 0;">
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Tip proces:</strong></span>
              <span><strong>${rezultat.tipProces}</strong></span>
            </div>
            
            ${rezultat.valoareCerere !== 'N/A' ? `
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Valoarea cererii:</strong></span>
              <span><strong>${rezultat.valoareCerere} lei</strong></span>
            </div>
            ` : ''}
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 2px solid #ccc;">
              <span><strong>Taxa judiciară:</strong></span>
              <span style="color: #28a745; font-size: 18px;"><strong>${rezultat.taxaCalculata} lei</strong></span>
            </div>
          </div>
          
          <div style="margin-top: 20px; padding: 15px; background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 5px;">
            <h4 style="margin-bottom: 10px;">Calcul desfășurat:</h4>
            <p style="margin: 0; white-space: pre-line;">${rezultat.descriereCalcul}</p>
          </div>
          
          <div style="margin-top: 20px; padding: 15px; background-color: #e3f2fd; border: 1px solid #90caf9; border-radius: 5px;">
            <h4 style="margin-bottom: 10px;">Baza legală:</h4>
            <p style="margin: 0;">${rezultat.bazaLegala}</p>
          </div>
        </div>
      `;

      const opt = {
        margin: 1,
        filename: `taxa-judiciara-${tip}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      html2pdf().set(opt).from(element).save();
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <GavelIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Calculator Taxe Judiciare
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="taxe tabs"
          sx={{
            '& .MuiTab-root': {
              minWidth: 150,
              padding: '14px 28px',
              fontSize: '0.9rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: '25px 25px 0 0',
              margin: '0 2px',
              transition: 'all 0.3s ease-in-out',
              border: '2px solid #e0e0e0',
              borderBottom: 'none',
              backgroundColor: '#fafafa',
              color: '#757575',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#424242',
                color: '#212121',
                transform: 'translateY(-2px)',
              },
              '&.Mui-selected': {
                backgroundColor: '#424242',
                borderColor: '#424242',
                color: 'white',
                fontWeight: 600,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(66, 66, 66, 0.3)',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            }
          }}
        >
          <Tab label="Procese Civile" />
          <Tab label="Procese Comerciale" />
          <Tab label="Procese Administrative" />
        </Tabs>
      </Box>

      {/* Tab pentru Procese Civile */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
              <Typography variant="body2" color="success.main">
                <strong>Procese Civile:</strong> Taxele se calculează conform{' '}
                <Tooltip 
                  title={
                    <Box sx={{ backgroundColor: 'white', color: 'black', padding: 2, borderRadius: 1, border: '1px solid #ccc' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, color: 'black' }}>
                        OUG nr. 80/2013, Art. 3:
                      </Typography>
                      <Typography variant="body2" sx={{ marginBottom: 1, color: 'black' }}>
                        Acțiunile și cererile evaluabile în bani se taxează astfel:
                      </Typography>
                      <Typography variant="body2" component="div" sx={{ color: 'black' }}>
                        a) până la 500 lei - 8%, dar nu mai puțin de 20 lei;<br/>
                        b) între 501-5.000 lei - 40 lei + 7% pentru ce depășește 500 lei;<br/>
                        c) între 5.001-25.000 lei - 355 lei + 5% pentru ce depășește 5.000 lei;<br/>
                        d) între 25.001-50.000 lei - 1.355 lei + 3% pentru ce depășește 25.000 lei;<br/>
                        e) între 50.001-250.000 lei - 2.105 lei + 2% pentru ce depășește 50.000 lei;<br/>
                        f) peste 250.000 lei - 6.105 lei + 1% pentru ce depășește 250.000 lei.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        maxWidth: 500
                      }
                    },
                    arrow: {
                      sx: {
                        color: 'white',
                        '&::before': {
                          border: '1px solid #ccc'
                        }
                      }
                    }
                  }}
                >
                  <Link 
                    href={`https://ilegis.ro/oficiale/index/act/304333/data/${new Date().toISOString().split('T')[0]}/dataI/${new Date().toISOString().split('T')[0]}#idArt3`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    OUG nr. 80/2013
                  </Link>
                </Tooltip>
                . Formula progresivă în funcție de valoarea cererii.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Tip proces <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={tipProces}
                    onChange={(e) => setTipProces(e.target.value)}
                  >
                    <MenuItem value="patrimonial">Cerere patrimonială</MenuItem>
                    <MenuItem value="nepatrimonial">Cerere nepatrimonială</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {tipProces === 'patrimonial' && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Valoarea cererii (RON) <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={valoareCerere}
                    onChange={(e) => setValoareCerere(e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{ min: 0, step: 'any' }}
                  />
                </Grid>
              )}

              {tipProces === 'nepatrimonial' && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Tip cerere <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={tipCerereNepatrimoniala}
                      onChange={(e) => setTipCerereNepatrimoniala(e.target.value)}
                    >
                      {Object.entries(taxeNepatrimoniale).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value.descriere} - {value.taxa} lei
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Tip instanță <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={tipInstanta}
                    onChange={(e) => setTipInstanta(e.target.value)}
                  >
                    <MenuItem value="judecatorie">Judecătorie</MenuItem>
                    <MenuItem value="tribunal">Tribunal</MenuItem>
                    <MenuItem value="curte_apel">Curte de Apel</MenuItem>
                    <MenuItem value="inalta_curte">Înalta Curte</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Calea de atac <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={tipCale}
                    onChange={(e) => setTipCale(e.target.value)}
                  >
                    <MenuItem value="prima_instanta">Prima instanță</MenuItem>
                    <MenuItem value="apel">Apel (50% din taxa de bază)</MenuItem>
                    <MenuItem value="recurs">Recurs (100% din taxa de bază)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={resetCalculatorCivil}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                
                {rezultatCivil && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => exportaPDF(rezultatCivil, 'civil')}
                      sx={{ marginRight: 2 }}
                    >
                      Export PDF
                    </Button>
                    
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copiazaRezultat(rezultatCivil, 'civil')}
                    >
                      Copiază rezultatul
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {rezultatCivil && (
              <Paper 
                elevation={3} 
                sx={{ 
                  marginTop: 3, 
                  padding: 3, 
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #e9ecef',
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    color: '#495057',
                    marginBottom: 3
                  }}
                >
                  <GavelIcon sx={{ marginRight: 1 }} />
                  Taxa Judiciară Calculată
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Tip proces:
                      </Typography>
                      <Chip label={rezultatCivil.tipProces} color="primary" />
                    </Box>
                  </Grid>
                  
                  {rezultatCivil.valoareCerere !== 'N/A' && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          Valoarea cererii:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {rezultatCivil.valoareCerere} lei
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <Divider sx={{ marginY: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                        Taxa judiciară:
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                        {rezultatCivil.taxaCalculata} lei
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Divider sx={{ marginY: 2 }} />
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Calcul desfășurat:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      backgroundColor: '#ffffff',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #dee2e6',
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {rezultatCivil.descriereCalcul}
                  </Typography>
                </Box>
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Baza legală:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      backgroundColor: '#e3f2fd',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #90caf9',
                      color: '#1565c0'
                    }}
                  >
                    <Tooltip 
                      title={
                        <Box sx={{ backgroundColor: '#ffffff', color: '#000000', padding: 2, borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, color: '#000000' }}>
                            OUG nr. 80/2013, Art. 3:
                          </Typography>
                          <Typography variant="body2" sx={{ marginBottom: 1, color: '#000000' }}>
                            Acțiunile și cererile evaluabile în bani se taxează astfel:
                          </Typography>
                          <Typography variant="body2" component="div" sx={{ color: '#000000' }}>
                            a) până la 500 lei - 8%, dar nu mai puțin de 20 lei;<br/>
                            b) între 501-5.000 lei - 40 lei + 7% pentru ce depășește 500 lei;<br/>
                            c) între 5.001-25.000 lei - 355 lei + 5% pentru ce depășește 5.000 lei;<br/>
                            d) între 25.001-50.000 lei - 1.355 lei + 3% pentru ce depășește 25.000 lei;<br/>
                            e) între 50.001-250.000 lei - 2.105 lei + 2% pentru ce depășește 50.000 lei;<br/>
                            f) peste 250.000 lei - 6.105 lei + 1% pentru ce depășește 250.000 lei.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Link 
                        href={`https://ilegis.ro/oficiale/index/act/304333/data/${new Date().toISOString().split('T')[0]}/dataI/${new Date().toISOString().split('T')[0]}#idArt3`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#1565c0' }}
                      >
                        {rezultatCivil.bazaLegala}
                      </Link>
                    </Tooltip>
                  </Typography>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab pentru Procese Comerciale */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.main">
                <strong>Procese Comerciale:</strong> Taxele pentru tribunalele comerciale, proceduri de insolvență conform{' '}
                <Tooltip 
                  title={
                    <Box sx={{ backgroundColor: 'white', color: 'black', padding: 2, borderRadius: 1, border: '1px solid #ccc' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, color: 'black' }}>
                        Legea nr. 85/2014, Art. 71:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'black' }}>
                        Procedura de insolvență - taxele se stabilesc în funcție de valoarea activului debitorului 
                        și complexitatea procedurii. Taxa se plătește la deschiderea procedurii.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        maxWidth: 400
                      }
                    },
                    arrow: {
                      sx: {
                        color: 'white',
                        '&::before': {
                          border: '1px solid #ccc'
                        }
                      }
                    }
                  }}
                >
                  <Link 
                    href={`https://ilegis.ro/oficiale/index/act/304535/data/${new Date().toISOString().split('T')[0]}/dataI/${new Date().toISOString().split('T')[0]}#idArt71`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Legea nr. 85/2014
                  </Link>
                </Tooltip>
                {' '}și arbitraj comercial.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Tip proces comercial <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={tipProcesComercial}
                    onChange={(e) => setTipProcesComercial(e.target.value)}
                  >
                    <MenuItem value="comercial_general">Proces comercial general (2%)</MenuItem>
                    <MenuItem value="insolventa">Procedură insolvență (în funcție de activ)</MenuItem>
                    <MenuItem value="arbitraj">Arbitraj comercial (1%)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Valoarea cererii / activului (RON) <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={valoareComerciala}
                  onChange={(e) => setValoareComerciala(e.target.value)}
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 0, step: 'any' }}
                  helperText={
                    tipProcesComercial === 'insolventa' 
                      ? 'Valoarea activului pentru calculul taxei de insolvență' 
                      : tipProcesComercial === 'arbitraj'
                      ? 'Pentru arbitraj: 1% din valoare (min 100, max 10.000 lei)'
                      : 'Pentru procese comerciale: 2% din valoare'
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={resetCalculatorComercial}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                
                {rezultatComercial && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => exportaPDF(rezultatComercial, 'comercial')}
                      sx={{ marginRight: 2 }}
                    >
                      Export PDF
                    </Button>
                    
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copiazaRezultat(rezultatComercial, 'comercial')}
                    >
                      Copiază rezultatul
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {rezultatComercial && (
              <Paper 
                elevation={3} 
                sx={{ 
                  marginTop: 3, 
                  padding: 3, 
                  backgroundColor: '#fff8e1',
                  border: '2px solid #ffcc02',
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    color: '#f57c00',
                    marginBottom: 3
                  }}
                >
                  Taxa Comercială Calculată
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    Taxa judiciară:
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                    {rezultatComercial.taxaCalculata} lei
                  </Typography>
                </Box>
                
                <Divider sx={{ marginY: 2 }} />
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Calcul desfășurat:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      backgroundColor: '#ffffff',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #ffcc02'
                    }}
                  >
                    {rezultatComercial.descriereCalcul}
                  </Typography>
                </Box>
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Baza legală:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      backgroundColor: '#fff3e0',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #ffb74d',
                      color: '#e65100'
                    }}
                  >
                    <Tooltip 
                      title={
                        <Box sx={{ backgroundColor: '#ffffff', color: '#000000', padding: 2, borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, color: '#000000' }}>
                            {rezultatComercial.bazaLegala.includes('85/2014') ? 'Legea nr. 85/2014, Art. 71:' : 
                             rezultatComercial.bazaLegala.includes('80/2013') ? 'OUG nr. 80/2013, Art. 3:' : 
                             'Regulamentul de arbitraj comercial:'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#000000' }}>
                            {rezultatComercial.bazaLegala.includes('85/2014') ? 
                              'Dacă cererea debitorului corespunde condițiilor, judecătorul-sindic va pronunța o încheiere de deschidere a procedurii generale sau simplificate. Taxa se stabilește în funcție de valoarea activului și complexitatea procedurii.' :
                             rezultatComercial.bazaLegala.includes('80/2013') ?
                              'Acțiunile și cererile evaluabile în bani la tribunalele comerciale se taxează conform acelorași reguli ca pentru procesele civile patrimoniale.' :
                              'Taxele pentru arbitrajul comercial se stabilesc conform regulamentului specific al instituției de arbitraj, de regulă între 1-3% din valoarea litigiului.'}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Link 
                        href={
                          rezultatComercial.bazaLegala.includes('85/2014') ?
                            `https://ilegis.ro/oficiale/index/act/304535/data/${new Date().toISOString().split('T')[0]}/dataI/${new Date().toISOString().split('T')[0]}#idArt71` :
                          rezultatComercial.bazaLegala.includes('80/2013') ?
                            `https://ilegis.ro/oficiale/index/act/304333/data/${new Date().toISOString().split('T')[0]}/dataI/${new Date().toISOString().split('T')[0]}#idArt3` :
                            '#'
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#e65100' }}
                      >
                        {rezultatComercial.bazaLegala}
                      </Link>
                    </Tooltip>
                  </Typography>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab pentru Procese Administrative */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#f3e5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="secondary.main">
                <strong>Procese Administrative:</strong> Taxele pentru contenciosul administrativ conform{' '}
                <Tooltip 
                  title={
                    <Box sx={{ backgroundColor: 'white', color: 'black', padding: 2, borderRadius: 1, border: '1px solid #ccc' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, color: 'black' }}>
                        Legea nr. 554/2004, Art. 21:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'black' }}>
                        Constituie motiv de revizuire pronunțarea hotărârilor prin încălcarea principiului 
                        priorității dreptului Uniunii Europene. Cererea de revizuire se introduce în termen 
                        de o lună de la comunicarea hotărârii definitive.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        maxWidth: 400
                      }
                    },
                    arrow: {
                      sx: {
                        color: 'white',
                        '&::before': {
                          border: '1px solid #ccc'
                        }
                      }
                    }
                  }}
                >
                  <Link 
                    href={`https://ilegis.ro/oficiale/index/act/298762/data/${new Date().toISOString().split('T')[0]}/dataI/${new Date().toISOString().split('T')[0]}#idArt21`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Legea nr. 554/2004
                  </Link>
                </Tooltip>
                , fiscal și alte proceduri administrative.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Tip proces administrativ <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={tipProcesAdministrativ}
                    onChange={(e) => setTipProcesAdministrativ(e.target.value)}
                  >
                    {Object.entries(taxeAdministrative).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value.descriere} - {value.taxa} lei
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {tipProcesAdministrativ === 'contencios_fiscal' && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Valoarea litigiului (RON)
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={valoareAdministrativa}
                    onChange={(e) => setValoareAdministrativa(e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{ min: 0, step: 'any' }}
                    helperText="Pentru contencios fiscal: 1% din valoare (max 5000 lei)"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={resetCalculatorAdministrativ}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                
                {rezultatAdministrativ && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => exportaPDF(rezultatAdministrativ, 'administrativ')}
                      sx={{ marginRight: 2 }}
                    >
                      Export PDF
                    </Button>
                    
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copiazaRezultat(rezultatAdministrativ, 'administrativ')}
                    >
                      Copiază rezultatul
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {rezultatAdministrativ && (
              <Paper 
                elevation={3} 
                sx={{ 
                  marginTop: 3, 
                  padding: 3, 
                  backgroundColor: '#fce4ec',
                  border: '2px solid #f48fb1',
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    color: '#c2185b',
                    marginBottom: 3
                  }}
                >
                  Taxa Administrativă Calculată
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    Taxa judiciară:
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#c2185b' }}>
                    {rezultatAdministrativ.taxaCalculata} lei
                  </Typography>
                </Box>
                
                <Divider sx={{ marginY: 2 }} />
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Calcul desfășurat:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      backgroundColor: '#ffffff',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #f48fb1'
                    }}
                  >
                    {rezultatAdministrativ.descriereCalcul}
                  </Typography>
                </Box>
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Baza legală:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      backgroundColor: '#f3e5f5',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #ce93d8',
                      color: '#7b1fa2'
                    }}
                  >
                    <Tooltip 
                      title={
                        <Box sx={{ backgroundColor: '#ffffff', color: '#000000', padding: 2, borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, color: '#000000' }}>
                            Legea nr. 554/2004, Art. 21:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#000000' }}>
                            Constituie motiv de revizuire, care se adaugă la cele prevăzute de Codul de procedură civilă, 
                            pronunțarea hotărârilor rămase definitive prin încălcarea principiului priorității dreptului 
                            Uniunii Europene. Cererea de revizuire se introduce în termen de o lună de la data comunicării 
                            hotărârii definitive și se soluționează de urgență și cu precădere.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Link 
                        href={`https://ilegis.ro/oficiale/index/act/298762/data/${new Date().toISOString().split('T')[0]}/dataI/${new Date().toISOString().split('T')[0]}#idArt21`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#7b1fa2' }}
                      >
                        {rezultatAdministrativ.bazaLegala}
                      </Link>
                    </Tooltip>
                  </Typography>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>
      </TabPanel>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Rezultatul a fost copiat în clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaxeJudiciare;